/*eslint-disable*/
import React, { useMemo, useContext } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'

import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import UploadSiteFiles from 'components/SitesUploads'
import Uploader from '../../../about/uploader'
import { requestUpdateEcosystemFiles, requestDeleteEcosystemFiles, requestGetEcosystemFiles } from 'api/ecoSystem'
import {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_SUPERSEDED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
} from 'constants/siteStatuses'
import { showErrorToast } from 'utils/toast'
import { ProfileContext } from 'providers/profile'
import { canAccessBlobExplorer } from 'utils/rolePermissions'

/**
 * EcosystemDocsUploader Component
 * 
 * Behavior Requirements:
 * 1. Visible during all phases of proposal (draft to confirmation)
 * 2. Following confirmation, documents should NOT be visible alongside the assessment
 * 3. On reassessment, uploads use the reassessment supporting-docs endpoint (same history id as the route)
 * 4. Enable it if there is one or more ecosystems assessments for the site
 * 5. Grey it out unless any ecosystems are listed
 * 
 * Implementation:
 * - Files are visible to Proposers/NCG only up to confirmation (not after)
 * - Upload/delete are disabled when site is confirmed or not in draft/edit-only status
 */
const EcosystemDocsUploader = ({ site, ecosystems = [] }) => {
  const params = useParams()
  const { profile } = useContext(ProfileContext) || {}

  const siteId = site?.id || params.id
  const siteHistoryId = params.siteHistoryId || site?.sitehistoryId || null
  const isReassessment = Boolean(siteHistoryId)
  const ecosystemsCount = ecosystems?.length ?? 0
  const hasEcosystems = ecosystemsCount > 0
  const roleId = profile?.Role?.id ?? profile?.RoleId
  const isKbaSecOrRfp = canAccessBlobExplorer(roleId)

  // Always pass siteHistoryId into the uploader when present. Stripping it for "blank slate"
  // forced POST /api/eco-system/{id}/supporting-docs during reassessment instead of
  // PATCH .../reassessment/sites/{historyId}/eco-systems/supporting-docs → 400 from API.

  // Site status checks
  const isConfirmedOrLater = [
    SITE_STATUS_CONFIRMED_ID,
    SITE_STATUS_SUPERSEDED_ID,
    SITE_STATUS_DELISTED_ID
  ].includes(site.SiteStatusId)

  // Visibility logic:
  // - Proposers/NCG: visible only up to confirmation (not after)
  // - Not visible after confirmation
  const shouldShow = hasEcosystems && (!isConfirmedOrLater || isKbaSecOrRfp)

  // Upload/delete should be disabled when:
  // 1. Site is confirmed or later (read-only for proposers)
  // 2. Site is not in draft or edit-only status (no editing allowed)
  // 3. No ecosystems are listed (greyed out)
  const editableStatuses = [SITE_STATUS_DRAFT_ID, SITE_STATUS_EDIT_ONLY_ID, SITE_STATUS_SUBMITTED_EDIT_ONLY_ID]
  const disabled = !editableStatuses.includes(site.SiteStatusId) || isConfirmedOrLater || !hasEcosystems

  const uploaderParams = useMemo(() => {
    return {
      id: siteId,
      siteHistoryId: siteHistoryId ?? null
    }
  }, [siteId, siteHistoryId])

  // Don't render if no ecosystems or not visible
  if (!siteId || !shouldShow) return null

  return (
    <Container>
      <Row>
        <Col md={8} mdOffset={2}>
          <div style={{ marginBottom: '12px' }}>
            <UploadSiteFiles
              label="uploadEcosystemsDocumentation"
              field="uploadEcosystemsDocumentation"
              infoTooltip="uploadEcosystemsDocumentationHint"
              height="128px"
              dataTest="upload-ecosystem-docs"
              isOptional={true}
              disabled={disabled || !hasEcosystems}
            />
          </div>
          <div style={{ padding: '8px 12px 12px 12px' }}>
            <Uploader
              key={`${uploaderParams.id || 'site'}-${uploaderParams.siteHistoryId || 'assessment'}-ecosystems-${hasEcosystems}`}
              params={uploaderParams}
              siteFiles={[]}
              disabled={disabled}
              uploadFn={async (uploadSiteId, uploadSiteHistoryId, formData, options) => {
                try {
                  // Ensure siteId is a primitive value
                  const actualSiteId = typeof uploadSiteId === 'object' ? (uploadSiteId?.id || uploadSiteId?.siteId) : uploadSiteId
                  const actualHistoryId = typeof uploadSiteHistoryId === 'object' ? (uploadSiteHistoryId?.siteHistoryId || uploadSiteHistoryId?.historyId) : uploadSiteHistoryId
                  
                  const response = await requestUpdateEcosystemFiles(actualSiteId, actualHistoryId, formData, options)
                  return response
                } catch (error) {
                  showErrorToast(<FormattedMessage id="uploadFailed" />)
                  throw error
                }
              }}
              deleteFn={async (deleteSiteId, deleteSiteHistoryId, fileId) => {
                try {
                  // Ensure siteId is a primitive value
                  const actualSiteId = typeof deleteSiteId === 'object' ? (deleteSiteId?.id || deleteSiteId?.siteId) : deleteSiteId
                  const actualHistoryId = typeof deleteSiteHistoryId === 'object' ? (deleteSiteHistoryId?.siteHistoryId || deleteSiteHistoryId?.historyId) : deleteSiteHistoryId
                  
                  const response = await requestDeleteEcosystemFiles(actualSiteId, actualHistoryId, fileId)
                  return response
                } catch (error) {
                  showErrorToast(<FormattedMessage id="deleteFailed" />)
                  throw error
                }
              }}
              fetchFilesFn={async (fetchSiteId, fetchSiteHistoryId) => {
                try {
                  const actualSiteId =
                    typeof fetchSiteId === 'object' ? (fetchSiteId?.id || fetchSiteId?.siteId) : fetchSiteId
                  const actualHistoryId =
                    typeof fetchSiteHistoryId === 'object'
                      ? (fetchSiteHistoryId?.siteHistoryId || fetchSiteHistoryId?.historyId)
                      : fetchSiteHistoryId

                  if (actualHistoryId) {
                    return await requestGetEcosystemFiles(actualSiteId, actualHistoryId)
                  }
                  return await requestGetEcosystemFiles(actualSiteId, null)
                } catch (error) {
                  console.error('Failed to fetch ecosystem files:', error)
                  return { files: [] }
                }
              }}
              fileType="ecosystems"
              allowDownloadsWhenConfirmed={isKbaSecOrRfp}
              hideDownload={isConfirmedOrLater && !isKbaSecOrRfp}
            />
          </div>
        </Col>
      </Row>
    </Container>
  )
}

export default EcosystemDocsUploader

EcosystemDocsUploader.propTypes = {
  site: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sitehistoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    SiteStatusId: PropTypes.number.isRequired
  }).isRequired,
  ecosystems: PropTypes.arrayOf(PropTypes.object)
}
